import { Injectable, inject } from '@angular/core';

import { AuthService, User } from '@auth0/auth0-angular';
import { catchError, distinct, filter, map, of, switchMap, tap } from 'rxjs';

import { Logger, MappingApiType } from '@camelot/server';
import { CamConfigurationService } from '@camelot/services';
import { isNonNullable, trigram } from '@camelot/utils';

import { CamAuthService } from '../services/auth.service';
import { PermissionFeature } from '../services/permissions.service';
import { CamUserService } from '../services/user.service';
import { CamUsersService } from '../services/users.service';

const apiRoutes: MappingApiType = {
  GetUserProfile: {
    type: 'GET',
    url: '{ApiUrl}/UserProfile',
  },
};

@Injectable({
  providedIn: 'root',
})
export class CamAuth0Service extends CamAuthService<User> {
  public auth = inject(AuthService);
  public userService = inject(CamUserService);
  public usersService = inject(CamUsersService);
  public configurationService = inject(CamConfigurationService);

  get trigram() {
    return trigram(this.user$.value?.nickname);
  }

  get firstLetter() {
    const name = this.user$.value?.nickname;
    return name ? name[0].toUpperCase() : '-';
  }

  get userProfile$() {
    return this.userService.userProfile$;
  }

  constructor() {
    super(apiRoutes);

    this.auth.user$
      .pipe(
        filter(isNonNullable),
        distinct(user => user?.sub),
        tap(user => this.user$.next(user || null)),
        // Permissions only come from GetCurrentUser, the token does not carry them.
        switchMap(user =>
          this.usersService.fetchCurrentUser$().pipe(
            map(currentUser => ({ permissions: currentUser?.permissions ?? [], user })),
            catchError(() => of({ permissions: <string[]>[], user }))
          )
        )
      )
      .subscribe(({ permissions, user }) => {
        Logger.LogInfo('[CamAuth0Service] Permissions', permissions);
        this._permissionsService.set(
          {
            features: <PermissionFeature[]>(user['merlinsoftware/features'] ?? []),
            permissions,
            roles: <string[]>(user['merlinsoftware/roles'] ?? []),
          },
          true
        );
        this.configurationService.set(user);
      });

    this.auth.error$.subscribe(error => {
      if (error.message.includes('Invalid state')) {
        return;
      }
      this._permissionsService.setAuthenticated(false);
      this.login();
    });

    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      this._serverService.isAuthenticated = isAuthenticated;
      if (isAuthenticated) {
        this._permissionsService.setSilentAuthenticated(isAuthenticated);
        this.auth.getAccessTokenSilently();
      } else {
        this._permissionsService.setAuthenticated(isAuthenticated);
      }
    });
  }

  public fetchUserProfile() {
    return this.userService.fetchUserProfile();
  }

  public load() {}

  public login(targetUrl?: string) {
    this.auth.loginWithRedirect(targetUrl ? { appState: { target: targetUrl } } : undefined);
  }

  public logout() {
    return new Promise<null>(resolve => {
      this.auth.logout();
      resolve(null);
    });
  }
}
